/**
 * Audio Lessons Store — Named Selectors
 *
 * Referentially-stable selector functions for `useAudioLessonsStore`. Import
 * these instead of inline `(s) => s.foo` selectors so screens stay
 * CHAT_PERFORMANCE-compliant (individual, stable selectors → minimal re-renders).
 *
 * AudioLessonsState is not exported, so the type is re-derived via
 * `ReturnType<typeof useAudioLessonsStore.getState>`.
 */

import { useAudioLessonsStore } from './audioLessonsStore';
import type { AudioEpisodeProgress } from './audioLessonsStore';

type AudioLessonsState = ReturnType<typeof useAudioLessonsStore.getState>;

// ── Catalog (series list) ────────────────────────────────────────────
export const selectAudioSeries = (s: AudioLessonsState) => s.series;
export const selectIsLoadingAudioSeries = (s: AudioLessonsState) => s.isLoadingSeries;
export const selectAudioSeriesError = (s: AudioLessonsState) => s.seriesError;
export const selectAudioSeriesCourseId = (s: AudioLessonsState) => s.seriesCourseId;

// ── Series detail (episode list) ─────────────────────────────────────
export const selectCurrentAudioSeries = (s: AudioLessonsState) => s.currentSeries;
export const selectAudioEpisodes = (s: AudioLessonsState) => s.episodes;
export const selectIsLoadingAudioEpisodes = (s: AudioLessonsState) => s.isLoadingEpisodes;
export const selectAudioEpisodesError = (s: AudioLessonsState) => s.episodesError;
export const selectCurrentAudioSeriesSlug = (s: AudioLessonsState) => s.currentSeriesSlug;

// ── Episode detail (player) ──────────────────────────────────────────
export const selectCurrentAudioEpisode = (s: AudioLessonsState) => s.currentEpisode;
export const selectCurrentAudioRendition = (s: AudioLessonsState) => s.currentRendition;
export const selectCurrentAudioTranscript = (s: AudioLessonsState) => s.currentTranscript;
export const selectCurrentAudioEpisodeSlug = (s: AudioLessonsState) => s.currentEpisodeSlug;
export const selectAudioTeachingLanguage = (s: AudioLessonsState) => s.teachingLanguage;
export const selectAudioMissingTeachingLanguages = (s: AudioLessonsState) => s.missingTeachingLanguages;
export const selectIsLoadingAudioEpisode = (s: AudioLessonsState) => s.isLoadingEpisode;
export const selectAudioEpisodeError = (s: AudioLessonsState) => s.episodeError;
export const selectIsAudioEpisodeForbidden = (s: AudioLessonsState) => s.isEpisodeForbidden;

// ── Progress ─────────────────────────────────────────────────────────
export const selectAudioProgressByEpisode = (s: AudioLessonsState) => s.progressByEpisode;
/**
 * Progress for a single episode. Returns the stored entry (stable reference) or
 * a constant `null` — never a fresh object — so it is safe with Zustand v5's
 * snapshot caching.
 */
export const selectAudioEpisodeProgress =
  (slug: string) =>
  (s: AudioLessonsState): AudioEpisodeProgress | null =>
    s.progressByEpisode[slug] ?? null;

/**
 * The cached linked-episode slug for a lesson, or `null` when none is known yet
 * (not looked up) or none exists. Returns a primitive (string | null), so it is
 * safe with Zustand v5 snapshot caching.
 */
export const selectLinkedEpisodeSlug =
  (lessonSlug: string | undefined) =>
  (s: AudioLessonsState): string | null =>
    lessonSlug ? s.linkedEpisodeByLesson[lessonSlug] ?? null : null;

// ── Actions ──────────────────────────────────────────────────────────
export const selectFetchAudioSeriesList = (s: AudioLessonsState) => s.fetchSeriesList;
export const selectFetchAudioSeries = (s: AudioLessonsState) => s.fetchSeries;
export const selectFetchAudioEpisode = (s: AudioLessonsState) => s.fetchEpisode;
export const selectSaveAudioProgress = (s: AudioLessonsState) => s.saveProgress;
export const selectFetchAudioProgress = (s: AudioLessonsState) => s.fetchProgress;
export const selectResolveLinkedEpisode = (s: AudioLessonsState) => s.resolveLinkedEpisode;
export const selectHydrateAudioProgress = (s: AudioLessonsState) => s.hydrateProgress;
export const selectResetAudioPlayer = (s: AudioLessonsState) => s.resetPlayer;
export const selectClearAudioSeriesError = (s: AudioLessonsState) => s.clearSeriesError;
export const selectClearAudioEpisodesError = (s: AudioLessonsState) => s.clearEpisodesError;
export const selectClearAudioEpisodeError = (s: AudioLessonsState) => s.clearEpisodeError;
